import React, { useEffect, useState } from "react";
import { Home, Briefcase, MapPin, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import LocationPicker from "./LocationPicker";
import { addressApi, errorMessage } from "../../lib/api";

const EMPTY = { label: "", house_no: "", floor: "", landmark: "", directions: "", lat: null, lng: null };
const detailLine = (a) => [a.house_no, a.floor && `Floor ${a.floor}`, a.landmark].filter(Boolean).join(" · ");

// Edit/delete the customer's saved addresses, including route details (house no, floor,
// landmark, directions for the driver). Reused from the ride and food screens.
export default function ManageAddressesDialog({ open, onOpenChange, onChanged }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busyId, setBusyId] = useState(null);
  const [pinFor, setPinFor] = useState(null);

  const load = () => { setLoading(true); addressApi.list().then((l) => setAddresses(l || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { if (open) load(); }, [open]);

  const startEdit = (a) => {
    setEditId(a.address_id);
    setPinFor(null);
    setForm({ label: a.label || "", house_no: a.house_no || "", floor: a.floor || "", landmark: a.landmark || "", directions: a.directions || "", lat: a.lat ?? null, lng: a.lng ?? null });
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (a) => {
    if (!form.label.trim()) return toast.error("Label can't be empty");
    setBusyId(a.address_id);
    try {
      await addressApi.patch(a.address_id, {
        label: form.label.trim(),
        house_no: form.house_no.trim(),
        floor: form.floor.trim(),
        landmark: form.landmark.trim(),
        directions: form.directions.trim(),
        ...(form.lat != null ? { lat: form.lat, lng: form.lng } : {}),
      });
      setEditId(null); setPinFor(null); load(); onChanged?.(); toast.success("Address updated");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };
  const remove = async (a) => {
    setBusyId(a.address_id);
    try { await addressApi.remove(a.address_id); load(); onChanged?.(); toast.success("Address removed"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manage-addresses-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-brand" /> Saved addresses</DialogTitle>
          <DialogDescription>Add a house/flat number, floor, landmark and directions so your driver finds you easily.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-2 overflow-y-auto">
          {loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
          {!loading && addresses.length === 0 && <p className="py-6 text-center text-sm text-ink-muted">No saved addresses yet. Save a drop as Home or Work to see it here.</p>}
          {addresses.map((a) => {
            const Ic = (a.label || "").toLowerCase() === "home" ? Home : (a.label || "").toLowerCase() === "work" ? Briefcase : MapPin;
            const editing = editId === a.address_id;
            const details = detailLine(a);
            return (
              <div key={a.address_id} className="rounded-xl border border-surface-300 p-3" data-testid={`manage-address-${a.address_id}`}>
                {editing ? (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs font-medium text-ink-soft">Label</label>
                      <Input value={form.label} onChange={set("label")} className="h-9" data-testid="manage-address-label-input" autoFocus />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-medium text-ink-soft">House / Flat no.</label>
                        <Input value={form.house_no} onChange={set("house_no")} placeholder="e.g. B-204" className="h-9" data-testid="manage-address-house-input" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-ink-soft">Floor</label>
                        <Input value={form.floor} onChange={set("floor")} placeholder="e.g. 3rd" className="h-9" data-testid="manage-address-floor-input" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-ink-soft">Landmark</label>
                      <Input value={form.landmark} onChange={set("landmark")} placeholder="e.g. near State Bank" className="h-9" data-testid="manage-address-landmark-input" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-ink-soft">Directions for driver</label>
                      <textarea value={form.directions} onChange={set("directions")} rows={2} placeholder="e.g. Blue gate, ring the top bell" className="w-full rounded-md border border-surface-300 bg-white p-2 text-sm" data-testid="manage-address-directions-input" />
                    </div>
                    <div>
                      <button type="button" onClick={() => setPinFor(pinFor === a.address_id ? null : a.address_id)} className="inline-flex items-center gap-1.5 rounded-full border border-surface-300 px-3 py-1.5 text-xs font-semibold text-ink-soft hover:text-brand" data-testid={`manage-address-pin-toggle-${a.address_id}`}><MapPin className="h-3.5 w-3.5" /> {form.lat != null ? "Adjust exact door pin" : "Set exact door pin"}</button>
                      {pinFor === a.address_id && (
                        <div className="mt-2" data-testid="manage-address-pin">
                          <LocationPicker value={{ lat: form.lat ?? undefined, lng: form.lng ?? undefined, address: a.address }} onChange={(p) => setForm((f) => ({ ...f, lat: p.lat, lng: p.lng }))} />
                          <p className="mt-1 text-xs text-green-700">Pin set — drag the map to place it exactly on your door.</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => save(a)} disabled={busyId === a.address_id} className="bg-brand text-white hover:bg-brand-600" data-testid={`manage-address-save-${a.address_id}`}>{busyId === a.address_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <Ic className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink">{a.label || "Address"}</p>
                      <p className="truncate text-xs text-ink-soft">{a.address}</p>
                      {details && <p className="mt-0.5 truncate text-xs text-ink-muted" data-testid={`manage-address-details-${a.address_id}`}>{details}</p>}
                      {a.directions && <p className="mt-0.5 truncate text-xs italic text-ink-muted">“{a.directions}”</p>}
                      {a.lat != null && a.lng != null && (
                        <img src={addressApi.staticMapUrl(a.lat, a.lng)} alt="Saved door pin" loading="lazy" className="mt-2 h-20 w-full max-w-[220px] rounded-lg border border-surface-200 object-cover" data-testid={`manage-address-map-${a.address_id}`} />
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(a)} data-testid={`manage-address-edit-${a.address_id}`}><Pencil className="h-3.5 w-3.5 text-ink-soft" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(a)} disabled={busyId === a.address_id} data-testid={`manage-address-delete-${a.address_id}`}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
